import type { Role } from 'src/auth/types';
import type { Transcript } from 'src/types/script';
import type { IFileItem, IIntentItem } from 'src/types/file';
import type { NavProps, NavItemProps } from 'src/components/navbar';

import { scroller } from 'react-scroll';

import dayjs from 'src/lib/dayjs';

import {
  type Data,
  type DemographicInfo,
  type IComplianceBorrower,
  EComplianceBorrowerFieldKey,
} from 'src/types/summary';

import { FAILED_SUMMARY_STATUSES } from './constants';
import {
  CallStatus,
  IntentTags,
  UploadStatus,
  PipelineType,
  RunConfigStatus,
  ApiSummaryStatus,
} from './enumerations';

interface FileValidationParams {
  pipelineType: PipelineType;
  audioFilesCount: number;
  pdfFilesCount: number;
  isPdfOnly?: boolean;
}

export const getTranscriptFeedbackValue = (transcript: Transcript): string => {
  if (!transcript) return '';
  if (typeof transcript === 'string') return transcript;
  return `${transcript.timestamp} - ${transcript.content}`;
};

export const mapSummaryStatus = (status: string) => {
  if (status === ApiSummaryStatus.SummarizationSavedOnline) {
    return CallStatus.Complete;
  }

  if (status === ApiSummaryStatus.SpeakerDiarizationComplete) {
    return CallStatus.Finalizing;
  }

  if (FAILED_SUMMARY_STATUSES.includes(status)) {
    return CallStatus.Failed;
  }

  return CallStatus.Processing;
};

/**
 * Extracts file information from an array of file objects and maps it to an array of IFileItem objects.
 *
 * @param files - An array of file objects to be mapped.
 * @returns An array of IFileItem objects with additional properties.
 */
export const mapDataToIFileItem = (files: any[]): IFileItem[] =>
  files.map((file) => ({
    ...file,
    created: new Date(`${file.timestamp}Z`), // Z added for explicit UTC, to convert to local time
    name: file.name,
    size: file.size,
    callId: file.call?.id,
    callStatus: file.call ? mapSummaryStatus(file.call.status) : CallStatus.Failed,
    callUpdated: file.call ? new Date(`${file.call.updated}Z`) : null, // Z added for explicit UTC, to convert to local time
  }));

export const getYesNoValue = (value: string | undefined) => {
  if (!value) return 'N/A';

  switch (value?.toUpperCase()) {
    case 'Y':
      return 'Yes';
    case 'N':
      return 'No';
    default:
      return capitalizeFirstLetter(value);
  }
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getFileErrorMessage = (status: UploadStatus): string => {
  switch (status) {
    case UploadStatus.GeneralError:
      return 'File upload failed.';
    case UploadStatus.FileSizeError:
      return 'File size exceeds 200 MB limit.';
    case UploadStatus.FileTypeError:
      return 'Invalid file type. Supported types: WAV, MP3, MP4.';
    default:
      return '';
  }
};

export const getRefetchFileTime = (size: number): number => {
  const mb = 1024 * 1024;
  const min = 60 * 1000;

  if (size < 10 * mb) {
    // size < 10MB
    return 1 * min; // 1 minute in milliseconds
  }
  if (size >= 10 * mb && size < 50 * mb) {
    // 10MB <= size < 50MB
    return 2 * min; // 2 minutes in milliseconds
  }
  if (size >= 50 * mb && size < 100 * mb) {
    // 50MB <= size < 100MB
    return 4 * min; // 4 minutes in milliseconds
  }
  // size >= 100MB
  return 5 * min; // 5 minutes in milliseconds
};

export const isTimeTwentyMinutesOld = (dateTime: Date): boolean => {
  const now = new Date();
  const twentyMinutesAgo = new Date(now.getTime() - 20 * 60 * 1000);
  return dateTime <= twentyMinutesAgo;
};

export const getSetOfSectionIds = (sections: any): Set<string> => {
  const _sections = new Set<string>();
  sections.map((section: { id: string }) => _sections.add(section.id));
  return _sections;
};

export const scrollToElement = (id: string) => {
  scroller.scrollTo(id, {
    offset: -80,
    smooth: true,
  });
};

/**
 * Filters navigation data based on user roles and permissions.
 * This function processes a navigation structure, retaining only the items and sub-items
 * that the user is authorized to see. It also dynamically assigns a `defaultChildPath`
 * to parent items, which defaults to the path of the first accessible child.
 *
 * @param navData - The original navigation data.
 * @param userRoles - The roles of the user.
 * @returns The filtered navigation data containing only the items that match the user's roles.
 */
export const getFilteredNavData = (
  navData: NavProps['data'],
  userRoles: Role[]
): NavProps['data'] => {
  const roleNames = userRoles.map((role) => role.role_name);
  const permissions = userRoles.flatMap((role) => role.permissions);

  return [
    {
      subheader: '',
      items: navData[0].items
        .filter((item) => item.roles?.some((role) => roleNames.includes(role)))
        .map((item) => {
          const filteredChildren = item.children?.filter(
            (child: NavItemProps) =>
              // Include items with no defined permissions OR with matching permissions
              // If no permissions are defined, the item is accessible by default
              !child.permissions ||
              child.permissions.some((permission: string) => permissions.includes(permission))
          );
          return {
            ...item,
            children: filteredChildren,
            defaultChildPath: filteredChildren?.[0]?.path, // Ensure defaultChildPath is set
          };
        }),
    },
  ];
};

export const getPdfFileUploadErrorMessage = (code: string): string => {
  switch (code) {
    case 'too-many-files':
      return 'Only one PDF file is allowed.';
    case 'file-too-large':
      return 'File size exceeds 200 MB limit.';
    case 'file-invalid-type':
      return 'Invalid file type. only PDF accepted.';
    default:
      return '';
  }
};

export const getAudioFileUploadErrorMessage = (code: string): string => {
  switch (code) {
    case 'file-too-large':
      return 'File size exceeds 200 MB limit.';
    case 'file-invalid-type':
      return 'Invalid file type. Supported types: WAV, MP3, MP4.';
    default:
      return '';
  }
};

/**
 * Returns the field value for a borrower from the form_1003 data
 * Updated to handle array fields (ethnicity, race)
 */
export const getBorrowerFieldValue = (
  borrower: IComplianceBorrower | null,
  fieldKey: EComplianceBorrowerFieldKey,
  index?: number
): Data => {
  const fallbackValue: Data = { value: '-', __path: '' };
  if (!borrower) return fallbackValue;

  // Handle name field
  if (fieldKey === EComplianceBorrowerFieldKey.NAME) {
    return borrower.borrower_name || fallbackValue;
  }

  // Handle missing field
  if (!borrower[fieldKey]) return fallbackValue;

  // Handle array fields (ethnicity and race)
  if (Array.isArray(borrower[fieldKey])) {
    const infoArray = borrower[fieldKey] as DemographicInfo[];

    // Return specific index if provided
    if (index !== undefined && infoArray[index]) {
      return infoArray[index].form_1003 || fallbackValue;
    }

    // Otherwise return first item (backwards compatibility)
    return infoArray[0]?.form_1003 || fallbackValue;
  }

  // Handle non-array fields (sex)
  return (borrower[fieldKey] as DemographicInfo).form_1003 || fallbackValue;
};

export const mapRunConfigStatus = (status: string) => {
  if (status === 'INPROGRESS') {
    return RunConfigStatus.InProgress;
  }

  if (status === 'COMPLETED') {
    return RunConfigStatus.Completed;
  }

  if (status === 'FAILED') {
    return RunConfigStatus.Failed;
  }

  return RunConfigStatus.Pending;
};

export const getRunConfigId = (intent: any) => {
  if (intent.pipeline_type === PipelineType.Servicing) {
    return intent.run_config ? intent.run_config.id : null;
  }
  return intent.run_config ? intent.run_config.id : null;
};

/**
 * Maps data to an array of IIntentItem objects.
 *
 * @param data - An array of data objects to be mapped.
 * @returns An array of IIntentItem objects with additional properties.
 */
export const mapDataToIIntentItem = (data: any[]): IIntentItem[] =>
  data.map((intent) => {
    const audioFile = intent.files.find((file: any) => file.extension !== 'pdf');
    const pdfFile = intent.files.find((file: any) => file.extension === 'pdf');

    return {
      id: intent.id,
      audioFileName: audioFile ? audioFile.name : '',
      audioFileId: audioFile ? audioFile.id : '',
      pdfFileName: pdfFile ? pdfFile.name : null,
      pdfFileId: pdfFile ? pdfFile.id : null,
      created: new Date(`${intent.created_at}Z`),
      runConfigUpdated: intent.run_config // Check if run_config exists
        ? intent.run_config.updated_at // If run_config exists, check if it has an updated_at timestamp
          ? new Date(`${intent.run_config.updated_at}Z`) // If updated_at exists, use it
          : new Date(`${intent.run_config.created_at}Z`) // If no updated_at, the run failed with no updates, use created_at
        : new Date(`${intent.created_at}Z`), // If no run_config at all, fallback to intent's created_at
      runConfigId: getRunConfigId(intent),
      runConfigStatus: (() => {
        if (
          intent.pipeline_type === PipelineType.Servicing ||
          intent.pipeline_type === PipelineType.Sales
        ) {
          return intent.run_config
            ? mapRunConfigStatus(intent.run_config.status)
            : RunConfigStatus.InProgress;
        }
        return pdfFile && intent.run_config
          ? mapRunConfigStatus(intent.run_config.status)
          : RunConfigStatus.Pending;
      })(),
      audioFileSize: audioFile ? audioFile.size : 0,
      pipelineType: intent.pipeline_type,
      lastName: '', // Assuming this value will be added later
    };
  });

/**
 * Validates if the files are valid to upload based on pipeline type
 * @param pipelineType - The type of pipeline being used
 * @param audioFilesCount - Number of audio files to upload
 * @param pdfFilesCount - Number of PDF files to upload
 * @param isPdfOnly - Whether only PDF upload is allowed (with existing audio reference)
 * @returns boolean indicating if the files are valid to upload
 */
export const areFilesValidToUpload = ({
  pipelineType,
  audioFilesCount,
  pdfFilesCount,
  isPdfOnly = false,
}: FileValidationParams): boolean => {
  if (pipelineType === PipelineType.Hmda) {
    return (
      (audioFilesCount === 1 && pdfFilesCount === 1) ||
      (audioFilesCount >= 1 && pdfFilesCount === 0) ||
      (pdfFilesCount === 1 && isPdfOnly)
    );
  }
  // For Servicing and Sales, just check for audio files
  return audioFilesCount >= 1;
};

export const getArrayItemLabel = (baseLabel: string, index: number): string =>
  index === 0 ? baseLabel : `${baseLabel} Sub`;

export interface CopilotMessage {
  role: string;
  content: {
    type: string;
    [key: string]: string | string[];
  }[];
}

/**
 * Converts start and end date strings in 'yyyy-mm-dd' format to Date objects.
 *
 * @param startDate - The start date as a string in 'yyyy-mm-dd' format.
 * @param endDate - The end date as a string in 'yyyy-mm-dd' format.
 * @returns An object containing startDate and endDate as Date objects.
 */
export function parseDateRange(
  startDate: string,
  endDate: string
): {
  startDate: Date;
  endDate: Date;
} {
  return {
    startDate: dayjs(startDate, 'YYYY-MM-DD').toDate(),
    endDate: dayjs(endDate, 'YYYY-MM-DD').toDate(),
  };
}

/**
 * Maps a PipelineType to its corresponding IntentTag.
 *
 * @param pipelineType - The pipeline type to map.
 * @returns The corresponding IntentTag for Sales, HMDA, and Servicing pipelines,
 *          or the original pipelineType string for unmapped types.
 */
export function getPipelineTag(pipelineType: PipelineType): IntentTags | string {
  switch (pipelineType) {
    case PipelineType.Sales:
      return IntentTags.Sales;
    case PipelineType.Hmda:
      return IntentTags.Hmda;
    case PipelineType.Servicing:
      return IntentTags.Servicing;
    default:
      return pipelineType;
  }
}
